import React, { useEffect } from 'react';

interface CSVInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TableRow: React.FC<{ name: string; description: string; example: string; required?: boolean }> = ({ name, description, example, required = false }) => (
    <tr className="border-b border-gray-200">
        <td className="py-2 px-4">
            <code className="bg-gray-200 text-gray-800 text-sm font-mono py-1 px-2 rounded">{name}</code>
            {required && <span className="text-red-500 ml-2">*</span>}
        </td>
        <td className="py-2 px-4 text-sm text-gray-700">{description}</td>
        <td className="py-2 px-4 text-sm text-gray-600"><em>{example}</em></td>
    </tr>
);


export const CSVInfoModal: React.FC<CSVInfoModalProps> = ({ isOpen, onClose }) => {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const exampleCSV = `receiptdate;businessname;amount;currency;category;paymentmethod;receipttime;tags
2023-10-27;Supermarket ABC;150,75;CZK;Potraviny;Kartou;18:30:00;Nakup,Zaruka`;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="csv-info-modal-title"
        >
            <div
                className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-3xl w-full max-h-[90vh] flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center pb-3 border-b">
                    <h2 id="csv-info-modal-title" className="text-xl font-bold text-gray-800">Informace o importu z CSV</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Zavřít"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="my-4 overflow-y-auto">
                    <p className="text-sm text-gray-600 mb-4">
                        Pro úspěšný import musí být soubor ve formátu CSV, kódovaný v <strong>UTF-8</strong> a jako oddělovač musí být použit <strong>středník (;)</strong>. První řádek souboru musí obsahovat názvy sloupců (hlavičku).
                    </p>

                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Struktura sloupců</h3>
                    <div className="overflow-x-auto border rounded-md">
                        <table className="min-w-full bg-white">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Název sloupce</th>
                                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Popis</th>
                                    <th className="py-2 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Příklad</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TableRow name="receiptdate" description="Datum na účtence" example="2023-12-24" required />
                                <TableRow name="businessname" description="Název podniku" example="Supermarket ABC" required />
                                <TableRow name="amount" description="Celková částka. Použijte čárku jako desetinný oddělovač." example="123,45" required />
                                <TableRow name="currency" description="Třípísmenný kód měny" example="CZK" required />
                                <TableRow name="category" description="Kategorie nákupu" example="Potraviny" required />
                                <TableRow name="paymentmethod" description="Způsob platby" example="Kartou" />
                                <TableRow name="receipttime" description="Čas na účtence (nepovinné)" example="14:35:00" />
                                <TableRow name="tags" description="Štítky oddělené čárkou" example="Nakup,Zaruka" />
                                <TableRow name="fileurl" description="URL odkaz na soubor účtenky" example="https://example.com/receipt.pdf" />
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-2"><span className="text-red-500">*</span> Označuje povinné sloupce.</p>


                    <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-2">Příklad CSV souboru</h3>
                    <pre className="bg-gray-100 p-4 rounded text-sm text-left whitespace-pre-wrap break-all">
                        <code>
                            {exampleCSV}
                        </code>
                    </pre>
                </div>


                <div className="mt-auto pt-4 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Zavřít
                    </button>
                </div>
            </div>
        </div>
    );
};